import React, {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import Layout from '../components/Layout';
import {api} from '../../api';
import {Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis} from 'recharts';

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => { api('/analytics/dashboard').then(r => r.ok ? r.json() : null).then(setData).catch(() => {}); }, []);

  if (!data) return <Layout><div className="text-gray-500">Loading...</div></Layout>;

  return (
    <Layout>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Dashboard</h2>
        <Link to="/analytics/sites" className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm">
          Manage Sites
        </Link>
      </div>

      {(!data.sites || data.sites.length === 0) ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <div className="text-4xl mb-4">📊</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No sites yet</h3>
          <p className="text-gray-500 mb-6">Add a website to start tracking visitors and calls.</p>
          <Link to="/analytics/sites" className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700">
            Add Your First Site
          </Link>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-sm text-gray-500">Total Pageviews</p>
              <p className="text-2xl font-bold text-gray-900">{data.totalPageviews}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-sm text-gray-500">Unique Visitors</p>
              <p className="text-2xl font-bold text-gray-900">{data.totalVisitors}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-sm text-gray-500">Events</p>
              <p className="text-2xl font-bold text-gray-900">{data.totalEvents}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-sm text-gray-500">Phone Calls</p>
              <p className="text-2xl font-bold text-gray-900">{data.totalCalls}</p>
            </div>
          </div>

          {data.pageviewsByDay && data.pageviewsByDay.length > 0 && (
            <div className="bg-white rounded-lg shadow p-4 mb-6">
              <h3 className="text-sm font-medium text-gray-500 mb-4">All Sites — Pageviews (Last 30 Days)</h3>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={data.pageviewsByDay}>
                  <XAxis dataKey="date" tick={{fontSize: 11}} />
                  <YAxis allowDecimals={false} tick={{fontSize: 11}} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#d97706" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          <h3 className="text-lg font-semibold text-gray-900 mb-3">Sites</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.sites.map(site => (
              <Link key={site.id} to={`/analytics/sites/${site.id}`}
                className="bg-white rounded-lg shadow p-5 hover:shadow-lg transition">
                <h4 className="font-semibold text-gray-900 mb-1">{site.name}</h4>
                {site.domain && <p className="text-xs text-gray-500 mb-3">{site.domain}</p>}
                <div className="flex gap-6 text-sm">
                  <div>
                    <p className="text-xl font-bold text-amber-600">{site.pageviews}</p>
                    <p className="text-xs text-gray-500">Pageviews</p>
                  </div>
                  <div>
                    <p className="text-xl font-bold text-amber-600">{site.visitors}</p>
                    <p className="text-xs text-gray-500">Visitors</p>
                  </div>
                  <div>
                    <p className="text-xl font-bold text-amber-600">{site.bounceRate != null ? `${site.bounceRate}%` : '—'}</p>
                    <p className="text-xs text-gray-500">Bounce Rate</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </>
      )}
    </Layout>
  );
}
